import { Box, Typography, Chip, Tooltip, alpha, useTheme } from '@mui/material'
import { useState, memo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from 'react-simple-maps'
import { Cloud, CheckCircle, Error as ErrorIcon } from '@mui/icons-material'

const geoUrl = 'https://unpkg.com/world-atlas@2.0.2/countries-110m.json'

const WorldMap = ({ monitorHost, monitorStatus, monitorType }) => {
  const theme = useTheme()
  const [hoveredRegion, setHoveredRegion] = useState(null)
  const [activeRequests, setActiveRequests] = useState([])

  // Monitoring bölgeleri - gerçek koordinatlar (longitude, latitude)
  const monitoringRegions = [
  {
    id: 'eu',
    name: 'Europe',
    country: 'Istanbul',
    coordinates: [28.9784, 41.0082],
    active: true,
    lastStatus: monitorStatus === 'down' ?  'error' : 'success',
    responseTime: 45,
  },
//   {
//     id: 'us',
//     name: 'North America',
//     country: 'USA',
//     coordinates: [-95.7129, 37.0902],
//     active: true,
//     lastStatus: 'success',
//     responseTime: 120,
//   },
//   {
//     id: 'asia',
//     name: 'Asia',
//     country: 'Singapore',
//     coordinates: [103.8198, 1.3521],
//     active: true,
//     lastStatus: 'error',
//     responseTime: 0,
//   },
//   {
//     id: 'au',
//     name: 'Australia',
//     country: 'Sydney',
//     coordinates: [151.2093, -33.8688],
//     active: true,
//     lastStatus: 'error',
//     responseTime: 0,
//   },
//   {
//     id: 'sa',
//     name: 'South America',
//     country: 'Brazil',
//     coordinates: [-47.8825, -15.7942],
//     active: true,
//     lastStatus: 'success',
//     responseTime: 0,
//   },
//   {
//     id: 'af',
//     name: 'Africa',
//     country: 'South Africa',
//     coordinates: [28.0473, -26.2041],
//     active: true,
//     lastStatus: 'error',
//     responseTime: 0,
//   },
 ]


  // Monitor host pozisyonu (haritanın üst ortasında)
  const monitorHostPosition = { x: 53, y: 10 } // percentage

  // Simüle edilmiş istek animasyonu
  useEffect(() => {
    const interval = setInterval(() => {
      const activeRegionsList = monitoringRegions.filter((r) => r.active)
      if (activeRegionsList.length > 0) {
        const randomRegion =
          activeRegionsList[Math.floor(Math.random() * activeRegionsList.length)]

        const requestId = Date.now()
        setActiveRequests((prev) => [
          ...prev,
          {
            id: requestId,
            from: randomRegion.coordinates,
            to: monitorHostPosition,
            status: randomRegion.lastStatus,
            regionId: randomRegion.id,
          },
        ])

        // İsteği 3 saniye sonra kaldır
        setTimeout(() => {
          setActiveRequests((prev) => prev.filter((req) => req.id !== requestId))
        }, 3000)
      }
    }, 4000) // Her 4 saniyede bir istek

    return () => clearInterval(interval)
  }, [])

  // Aktif bölgeler arasında statik bağlantı çizgileri
  const activeRegions = monitoringRegions.filter((r) => r.active)

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: "600px",
        height: 400,
        background: `radial-gradient(ellipse at center, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, transparent 70%)`,
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      {/* Grid pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          opacity: 0.3,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Animated background pulse */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: '80%',
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.15
          )} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      

      {/* Monitor Host - Cloud Icon */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        sx={{
          position: 'absolute',
          left: `${monitorHostPosition.x}%`,
          top: `${monitorHostPosition.y}%`,
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
        }}
      >
        
        {/* Cloud container with glow */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          
          
          {/* Pulse rings around cloud */}
          <Box
            component={motion.div}
            // animate={{
            //   scale: [0.8, 1.5, 0.8],
            //   opacity: [0.4, 0, 0.4],
            // }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut',
            }}
            sx={{
              position: 'absolute',
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.3
              )}, transparent)`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          

          {/* Cloud Icon */}
          <Box
            component={motion.div}
            animate={{
              y: [0, -5, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.2
              )}, ${alpha(theme.palette.primary.main, 0.1)})`,
              backdropFilter: 'blur(10px)',
              borderRadius: '50%',
              border: `2px solid ${alpha(monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main, 0.5)}`,
              boxShadow: `
                0 0 30px ${alpha(monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main, 0.4)},
                inset 0 0 20px ${alpha(monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main, 0.1)}
              `,
            }}
          >
            <Cloud
              sx={{
                fontSize: 30,
                color: monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
                filter: `drop-shadow(0 0 8px ${alpha(monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main, 0.6)})`,
              }}
            />

            {/* Status indicator */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -5,
                right: -5,
                width: 15,
                height: 15,
                borderRadius: '50%',
                background:
                  monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
                border: `2px solid ${theme.palette.background.paper}`,
                boxShadow: `0 0 10px ${
                  monitorStatus === 'up'
                    ? theme.palette.success.main
                    : monitorStatus === 'down'
                    ? theme.palette.error.main
                    : theme.palette.warning.main
                }`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {monitorStatus === 'up' ? (
                <CheckCircle sx={{ fontSize: 10, color: '#000' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 10, color: '#000' }} />
              )}
            </Box>
          </Box>

         
        </Box>
      </Box>

      {/* Animated Request Lines */}
      {(monitorStatus !== "maintenance" && monitorStatus !== "uncertain") && (
      <AnimatePresence>
        {activeRequests.map((request) => {
          // Koordinat dönüşümü
          const fromX = monitorType === "CronJobMonitor" ? 55 : ((request.from[0] + 180) / 360) * 100
          const fromY = monitorType === "CronJobMonitor" ? 15 : ((110 - request.from[1]) / 180) * 100

          // Region koordinatlarını yüzdeye çevir (yaklaşık)
          const toX = monitorType === "CronJobMonitor" ? ((request.from[0] + 180) / 360) * 100 : 55
          const toY = monitorType === "CronJobMonitor" ? ((110 - request.from[1]) / 180) * 100 : 15

          const isSuccess = request.status === 'success'

          return (
            <Box
              key={request.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 50,
              }}
            >
              {/* Request beam */}
              <svg
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                }}
              >
                <defs>
                  <linearGradient
                    id={`gradient-${request.id}`}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stopColor={
                        isSuccess
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                      stopOpacity="0"
                    />
                    <stop
                      offset="50%"
                      stopColor={
                        isSuccess
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                      stopOpacity="1"
                    />
                    <stop
                      offset="100%"
                      stopColor={
                        isSuccess
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                      stopOpacity="0"
                    />
                  </linearGradient>

                  <filter id={`glow-${request.id}`}>
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <motion.line
                  x1={`${fromX}%`}
                  y1={`${fromY}%`}
                  x2={`${toX}%`}
                  y2={`${toY}%`}
                  stroke={`url(#gradient-${request.id})`}
                  strokeWidth="3"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  exit={{ pathLength: 1, opacity: 0 }}
                  transition={{ duration: 1.5, ease: 'easeInOut' }}
                  filter={`url(#glow-${request.id})`}
                />
              </svg>

              {/* Traveling particle */}
              <motion.div
                initial={{
                  left: `${fromX}%`,
                  top: `${fromY}%`,
                  opacity: 0,
                  scale: 0,
                }}
                animate={{
                  left: `${toX}%`,
                  top: `${toY}%`,
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0],
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  duration: 1.5,
                  ease: 'easeInOut',
                  times: [0, 0.1, 0.9, 1],
                }}
                style={{
                  position: 'absolute',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: isSuccess
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  boxShadow: `0 0 20px ${
                    isSuccess
                      ? theme.palette.success.main
                      : theme.palette.error.main
                  }`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            </Box>
          )
        })}
      </AnimatePresence>        
      )}


      {/* World Map */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%',
        }}
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 147,
            center: [0, 20],
          }}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <defs>
            <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop
                offset="0%"
                style={{
                  stopColor: alpha(theme.palette.primary.main, 0.15),
                  stopOpacity: 1,
                }}
              />
              <stop
                offset="100%"
                style={{
                  stopColor: alpha(theme.palette.primary.main, 0.05),
                  stopOpacity: 1,
                }}
              />
            </linearGradient>

            <radialGradient id="pulseGradient">
              <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity="0.8" />
              <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Countries */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="url(#mapGradient)"
                  stroke={alpha(theme.palette.primary.main, 0.3)}
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: {
                      fill: alpha(theme.palette.primary.main, 0.2),
                      outline: 'none',
                      cursor: 'pointer',
                    },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Region markers */}
          {monitoringRegions.map((region) => (
            <Marker key={region.id} coordinates={region.coordinates}>
              <g>
                {/* Animated pulse rings */}
                {region.active && (
                  <>
                    <circle r={15} fill="url(#pulseGradient)" opacity={0.6}>
                      <animate
                        attributeName="r"
                        from="8"
                        to="30"
                        dur="2.5s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.6"
                        to="0"
                        dur="2.5s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    <circle r={10} fill="url(#pulseGradient)" opacity={0.8}>
                      <animate
                        attributeName="r"
                        from="5"
                        to="20"
                        dur="2s"
                        begin="0.5s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.8"
                        to="0"
                        dur="2s"
                        begin="0.5s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </>
                )}

                {/* Main marker */}
                <circle
                  r={region.active ? 7 : 5}
                  fill={
                    region.active
                      ? theme.palette.primary.main
                      : alpha(theme.palette.primary.main, 0.4)
                  }
                  stroke={
                    region.lastStatus === 'success'
                      ? theme.palette.success.main
                      : region.lastStatus === 'error'
                      ? theme.palette.error.main
                      : '#fff'
                  }
                  strokeWidth={2}
                  style={{
                    cursor: 'pointer',
                    filter: region.active
                      ? `drop-shadow(0 0 8px ${theme.palette.primary.main})`
                      : 'none',
                  }}
                  onMouseEnter={() => setHoveredRegion(region.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                />

                {/* Inner glow */}
                {region.active && (
                  <circle r={3} fill={alpha('#fff', 0.8)} opacity={0.8}>
                    <animate
                      attributeName="opacity"
                      values="0.5;1;0.5"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}

                {/* Status indicator badge */}
                {region.lastStatus && (
                  <g transform="translate(10, -10)">
                    <circle
                      r={5}
                      fill={
                        region.lastStatus === 'success'
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                      stroke="#fff"
                      strokeWidth={1}
                    />
                    {region.lastStatus === 'success' ? (
                      <text
                        x="0"
                        y="2"
                        textAnchor="middle"
                        fontSize="8"
                        fill="#fff"
                        fontWeight="bold"
                      >
                        ✓
                      </text>
                    ) : (
                      <text
                        x="0"
                        y="2"
                        textAnchor="middle"
                        fontSize="8"
                        fill="#fff"
                        fontWeight="bold"
                      >
                        ✕
                      </text>
                    )}
                  </g>
                )}
              </g>
            </Marker>
          ))}
        </ComposableMap>

        {/* Tooltips */}
        {monitoringRegions.map((region) => {
          if (hoveredRegion !== region.id) return null

          const x = ((region.coordinates[0] + 180) / 360) * 100
          const y = ((90 - region.coordinates[1]) / 180) * 100

          return (
            <Box
              key={`tooltip-${region.id}`}
              component={motion.div}
              initial={{ opacity: 0, y: 10, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              sx={{
                position: 'absolute',
                left: `${x}%`,
                top: `${y}%`,
                transform: 'translate(-50%, -120%)',
                whiteSpace: 'nowrap',
                background: alpha(theme.palette.background.paper, 0.98),
                backdropFilter: 'blur(10px)',
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                zIndex: 1000,
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {region.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#fff', 0.7),
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {region.country}
              </Typography>
              {region.active && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      color: '#000',
                    }}
                  />
                  {region.lastStatus && (
                    <Chip
                      label={region.lastStatus === 'success' ? 'OK' : 'FAIL'}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        background:
                          region.lastStatus === 'success'
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        color: '#000',
                      }}
                    />
                  )}
                </Box>
              )}
              {region.active && (
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.6),
                    fontSize: '0.7rem',
                    display: 'block',
                  }}
                >
                  Response: {region.responseTime}ms
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Stats overlay */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          boxShadow: `0 4px 20px ${alpha('#000', 0.2)}`,
          zIndex: 10,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: alpha('#fff', 0.6),
            fontSize: '0.65rem',
            display: 'block',
            mb: 0.5,
            letterSpacing: '0.5px',
          }}
        >
          MONITORING REGIONS
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: theme.palette.primary.main,
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.8)}`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              {monitoringRegions.filter((r) => r.active).length} Active
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: alpha(theme.palette.primary.main, 0.3),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: alpha('#fff', 0.5),
              }}
            >
              {monitoringRegions.filter((r) => !r.active).length} Inactive
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          boxShadow: `0 4px 20px ${alpha('#000', 0.2)}`,
          zIndex: 10,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: alpha('#fff', 0.6),
            fontSize: '0.65rem',
            fontWeight: 700,
            mb: 0.5,
            letterSpacing: '0.5px',
          }}
        >
          REQUEST STATUS
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: theme.palette.success.main,
              boxShadow: `0 0 10px ${alpha(theme.palette.success.main, 0.8)}`,
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            Success
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: theme.palette.error.main,
              boxShadow: `0 0 10px ${alpha(theme.palette.error.main, 0.8)}`,
            }}
          />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            Failed
          </Typography>
        </Box>
      </Box>

      {/* Scanning line */}
      <Box
        component={motion.div}
        animate={{
          y: ['-100%', '200%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        }}
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, 
            transparent, 
            ${alpha(theme.palette.primary.main, 0.5)}, 
            transparent)`,
          boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.6)}`,
          pointerEvents: 'none',
          zIndex: 5,
        }}
      />
    </Box>
  )
}

export default memo(WorldMap)